import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { CryptoType, PaymentCryptoStep } from '@pkg/types';

import { Events } from '@app/client';
import { registerEvent } from '@app/store/actions/events';
import { setLoader } from '@app/store/actions/loader';
import { setOrderItems } from '@app/store/actions/picker';
import { getCurrentUserEmojis, setCurrentUserEmoji } from '@app/store/actions/user-emojis';
import { LoaderType } from '@app/store/types/loader';

import { CryptoStepNotEnough, CryptoStepSendPayment, CryptoStepTooMuch } from './Steps';
import { CryptoStepOption } from './Steps/StepOption';

interface YatCryptoPaymentFlowProps {
    onBack: () => void;
}

type LocationStateType = {
    walletLinkCreation: string;
};

export const YatCryptoPaymentFlow = ({ onBack }: YatCryptoPaymentFlowProps) => {
    const { t } = useTranslation(['yat_picker']);
    const [cryptoStep, setCryptoStep] = useState<PaymentCryptoStep>(PaymentCryptoStep.option);
    const [cryptoType, setCryptoType] = useState<CryptoType>(CryptoType.BTC);
    const { orderItems } = useAppSelector((rootState) => rootState.picker);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const location = useLocation();
    const locationState = location.state as LocationStateType;
    const confirmCryptoOption = (type: CryptoType) => {
        setCryptoType(type);
        setCryptoStep(PaymentCryptoStep.sendPayment);
    };

    const walletLinkCreation = locationState?.walletLinkCreation ?? '';

    const totalPrice = useMemo(() => {
        return orderItems
            .filter((orderItem) => !orderItem.marked_invalid_at)
            .map((orderItem) => orderItem.unit_price_in_cents / 100)
            .reduce((a, b) => a + b, 0);
    }, [orderItems]);

    const emojisInCartList = useMemo(() => {
        return orderItems.map((orderItem) => orderItem.emoji_id).join(',');
    }, [orderItems]);

    const handleFinish = useCallback(() => {
        if (!orderItems.length) {
            return;
        }
        const validOrderItems = orderItems.filter((item) => !item.marked_invalid_at); //so we only display the actual purchased Yats on the confirmation screen

        dispatch(setLoader({ open: true, type: LoaderType.YatV2Loader, source: t('launching_yat_machine') }));
        dispatch(getCurrentUserEmojis());
        dispatch(setCurrentUserEmoji(validOrderItems[0].emoji_id));
        dispatch(setOrderItems([]));
        dispatch(setLoader({ open: false }));
        registerEvent(Events.purchased, {
            yat_unicode: emojisInCartList,
            payment_type: 'crypto',
            price: totalPrice,
            currency: cryptoType,
        });

        navigate(`/${orderItems[0].emoji_id}/success_crypto/${cryptoType}`, {
            state: {
                paymentSuccess: true,
                orderItemCount: orderItems.length,
                walletLinkCreation,
            },
        });
    }, [orderItems, cryptoType, walletLinkCreation, t, dispatch, navigate, emojisInCartList, totalPrice]);

    if (cryptoStep === PaymentCryptoStep.option) {
        return <CryptoStepOption onBack={onBack} onSelectCryptoType={confirmCryptoOption} />;
    }

    if (cryptoStep === PaymentCryptoStep.sendPayment) {
        return (
            <CryptoStepSendPayment
                onBack={() => setCryptoStep(PaymentCryptoStep.option)}
                onCancel={onBack}
                cryptoType={cryptoType}
                onNext={(step) => setCryptoStep(step)}
                onFinish={handleFinish}
            />
        );
    }

    if (cryptoStep === PaymentCryptoStep.tooMuch) {
        return <CryptoStepTooMuch onContinue={onBack} />;
    }

    if (cryptoStep === PaymentCryptoStep.notEnough) {
        return <CryptoStepNotEnough cryptoType={cryptoType} onBack={() => setCryptoStep(PaymentCryptoStep.option)} />;
    }

    return <CryptoStepOption onBack={onBack} onSelectCryptoType={confirmCryptoOption} />;
};
